import { omit } from 'lodash';

import FilterDefinition from '@models/FilterDefinition';
import { OrderBy } from '@models/OrderBy';
import { FilterGroup } from '@models/SearchFilter';
import { QueryCollectionData, SearchQuery } from '@models/SearchQuery';
import { buildOrderByStr } from '@modules/layouts/hooks/useSearchAndFilter/utils';

type CompatibilityKeys = 'distinctBy' | 'filter' | 'orderBy';

export type CompatibilityMap = Record<keyof Pick<SearchQuery, CompatibilityKeys>, boolean>;

const isFilterCompatible = (
  filterDefinitions: FilterDefinition[],
  filter?: FilterGroup
): boolean => {
  return (
    filter?.fields.every(({ filter, propertyName }) => {
      const filterDef = filterDefinitions.find(fd => fd.propertyName === propertyName);
      if (!filterDef) return false;
      return filter ? isFilterCompatible(filterDef.fields, filter) : true;
    }) ?? true
  );
};

const isDistinctByCompatible = (
  filterDefinitions: FilterDefinition[],
  distinctBy?: string
): boolean =>
  distinctBy ? filterDefinitions.some(({ propertyName }) => propertyName === distinctBy) : true;

// TODO: Make this recursive?
const isOrderByCompatible = (
  filterDefinitions: FilterDefinition[],
  orderBy: OrderBy[] | undefined = []
): boolean => {
  const hasOrderByField = (filterDefinitions: FilterDefinition[], field: string): boolean => {
    const split = field.split('/');
    const cur = split.shift();
    const filterDef = filterDefinitions.find(fd => fd.propertyName === cur);

    if (split.length === 0) return !!filterDef?.isSortable;

    return filterDef?.fields && filterDef.fields.length > 0
      ? hasOrderByField(filterDef?.fields, split.join('/'))
      : false;
  };

  return orderBy.every(({ field }) => hasOrderByField(filterDefinitions, field));
};

export const getCompatibilityMap = (
  filterDefs: FilterDefinition[] | undefined,
  { filter, orderBy, distinctBy }: Partial<SearchQuery>
) => {
  if (!filterDefs) return;

  const compatibilityMap: CompatibilityMap = {
    distinctBy: isDistinctByCompatible(filterDefs, distinctBy),
    filter: isFilterCompatible(filterDefs, filter),
    orderBy: isOrderByCompatible(filterDefs, orderBy),
  };

  return compatibilityMap;
};

export const buildQueryCollectionData = (searchQuery: SearchQuery): QueryCollectionData => ({
  ...omit(searchQuery, 'orderBy'),
  ...(searchQuery.orderBy.length > 0 ? { orderBy: buildOrderByStr(searchQuery.orderBy) } : {}),
});

export type SearchField<T> = (item: T) => unknown;

export type SearchItemsResult<T> = {
  items: T[];
  rankByItem: Map<T, number>;
};

const exactMatchRank = 0;
const containsMatchRank = 1;

const normalizeSearchValue = (value: unknown) => String(value ?? '').trim().toLowerCase();

export function searchItems<T>(
  items: T[],
  queryText: string,
  fields: Array<SearchField<T>>
): SearchItemsResult<T> {
  const query = normalizeSearchValue(queryText);

  if (!query) {
    return { items, rankByItem: new Map() };
  }

  const matches = items.flatMap((item, index) => {
    const values = fields.map(field => normalizeSearchValue(field(item))).filter(Boolean);

    const rank = values.some(value => value === query)
      ? exactMatchRank
      : values.some(value => value.includes(query))
        ? containsMatchRank
        : undefined;

    return rank == null ? [] : [{ index, item, rank }];
  });

  matches.sort((a, b) => a.rank - b.rank || a.index - b.index);

  return {
    items: matches.map(({ item }) => item),
    rankByItem: new Map(matches.map(({ item, rank }) => [item, rank])),
  };
}
